import Slot from '../domain/Slot';
import * as perf from '../utils/performance';
import {SizeMapping} from './sizeMappings';
import * as xandrAdManger from '../modules/xandrModule';
import * as optOutAdManager from '../modules/optOutModule';
import pubsub from '../utils/pubsub';
import Settings from "../domain/Settings";

const DEFAULT_XANDR_CPM_THRESHOLD = 999;

export enum AdServer {
	Xandr = 'xandr',
	OptOut = 'optOut',
}

export enum AdServerResolutionStrategy {
	Priority = 'priority',
	CPM = 'cpm'
}

interface AdManager {
	isEnabled(): boolean;

	init(): Promise<void> | void;

	defineSlot(slot: Slot): void;

	loadSlots(slots: Slot[]): Promise<void>;

	renderSlot(slot: Slot): void;

	removeSlot?(slot: Slot): void;

	// Targeting
	updatePageTargeting(): void;

	setUserPageOptions?(userIds: AnyObject[]): void;

	// Refreshing
	refreshSlots(slots: Slot[]): void;

	resetSlot(slot: Slot): void;

	// Size Management
	resizeSlot(slot: Slot, newSize: [number, number]): void;

	applySizeMapping?(slot: Slot, sizeMapping: SizeMapping): void;
}

const ADMANAGERS: Record<AdServer, AdManager> = {
	[AdServer.Xandr]: xandrAdManger,
	[AdServer.OptOut]: optOutAdManager
};

pubsub.subscribe('slot.received', onSlotReceived);

function getSlotAdManagers(slot: Slot): Record<AdServer, AdManager> {
	return Object.fromEntries(Object.entries(ADMANAGERS)
		.filter(([, adman]) => adman.isEnabled())
		.filter(([adServer]: [AdServer, AdManager]) => slot.supportedAdServers.includes(adServer))) as Record<AdServer, AdManager>;
}

function getAdManagers(): Record<AdServer, AdManager> {
	return Object.fromEntries(Object.entries(ADMANAGERS)
		.filter(([, adman]) => adman.isEnabled())) as Record<AdServer, AdManager>;
}

function getSlotsPerAdManager(slots: Slot[]): Array<{
	adManager: AdManager,
	slots: Slot[]
}> {
	const results: Partial<Record<AdServer, {
		adManager: AdManager,
		slots: Slot[]
	}>> = {};

	slots.forEach(slot => {
		slot.supportedAdServers
			.filter(adServer => ADMANAGERS[adServer].isEnabled())
			.forEach(adServer => {
				results[adServer] ??= {adManager: ADMANAGERS[adServer], slots: []};

				results[adServer].slots.push(slot);
			});
	});

	return Object.values(results);
}

export async function init() {
	perf.mark('admanager - init - start');

	await Promise.all(
		Object.values(ADMANAGERS)
			.map(adman => adman.init())
	);

	perf.mark('admanager - init - end');
}

export function defineSlot(slot: Slot) {
	Object.values(getSlotAdManagers(slot))
		.forEach(adman => adman.defineSlot(slot));
}

export function loadSlots(slots: Slot[]) {
	getSlotsPerAdManager(slots).forEach(({adManager, slots: adManagerSlots}) => {
		adManager.loadSlots(adManagerSlots);
	});
}

export async function renderSlot(slot: Slot) {
	if (!await waitForAllAdServerReceived(slot)) {
		// Probably already waiting
		return;
	}

	const winningAdServer = getWinningAdServer(slot);

	slot.setWinningAdServer(winningAdServer);

	ADMANAGERS[winningAdServer].renderSlot(slot);
}

const waitingForAdResponses: Record<Slot['name'], AnyFunction> = {};

async function waitForAllAdServerReceived(slot: Slot): Promise<boolean> {
	if (hasAllRequiredAdResponses(slot)) {
		return true;
	}

	if (waitingForAdResponses[slot.name]) {
		return false;
	}

	return new Promise((resolve) => {
		waitingForAdResponses[slot.name] = resolve;
	});
}

function onSlotReceived(slot: Slot) {
	if (waitingForAdResponses[slot.name] && hasAllRequiredAdResponses(slot)) {
		waitingForAdResponses[slot.name](true);
		delete waitingForAdResponses[slot.name];
	}
}

function hasAllRequiredAdResponses(slot: Slot): boolean {
	const expectedAdServers = Object.keys(getSlotAdManagers(slot)) as AdServer[];

	return expectedAdServers
		.filter(as => slot.getState(as).defined)
		.every(as => slot.adResponses[as]);
}

function getWinningAdServer(slot: Slot): AdServer {
	const adServersWithResponse = slot.supportedAdServers
		.filter(as => !(slot.adResponses[as]?.isEmpty ?? true));

	if (adServersWithResponse.length === 0) {
		return slot.supportedAdServers[0];
	}

	if (slot.adServerStrategy === AdServerResolutionStrategy.Priority) {
		return getPriorityBasedWinner(adServersWithResponse);
	}

	return getCpmBasedWinner(adServersWithResponse, slot);
}

function getPriorityBasedWinner(adServersWithResponse: AdServer[]): AdServer {
	return adServersWithResponse[0];
}

function getCpmBasedWinner(adServersWithResponse: AdServer[], slot: Slot) {
	const xandrCpm = slot.adResponses[AdServer.Xandr]?.cpm_publisher_currency,
		optOutCpm = slot.adResponses[AdServer.OptOut]?.cpm,
		xandrPriorityProductThreshold = Settings.getInstance().adServerResolution?.cpmStrategySettings?.priorityProductThreshold?.xandr
			?? DEFAULT_XANDR_CPM_THRESHOLD,
		isXandrPriorityProduct = xandrCpm >= xandrPriorityProductThreshold,
		isOptOutPriorityProduct = optOutCpm === 0 && slot.adResponses[AdServer.OptOut]?.optOutExt?.tags?.includes('is_sov');

	if (isXandrPriorityProduct && isOptOutPriorityProduct) {
		return getPriorityBasedWinner(adServersWithResponse);
	}

	if (isXandrPriorityProduct) {
		return AdServer.Xandr;
	}

	if (isOptOutPriorityProduct) {
		return AdServer.OptOut;
	}

	if (xandrCpm > optOutCpm) {
		return AdServer.Xandr;
	}

	if (xandrCpm < optOutCpm) {
		return AdServer.OptOut;
	}

	return getPriorityBasedWinner(adServersWithResponse);
}

export function resetSlot(slot: Slot) {
	Object.values(getAdManagers()).forEach(adManager => {
		adManager.resetSlot(slot);
	});
}

export function applySizeMapping(slot: Slot, sizeMapping: SizeMapping) {
	Object.values(getAdManagers()).forEach(adManager => {
		adManager.applySizeMapping?.(slot, sizeMapping);
	});
}

export function refreshSlots(slots: Slot[]) {
	const slotsByWinningAdServer = slots.reduce((acc, slot) => {
		if (!slot.winningAdServer)
			return acc;

		acc[slot.winningAdServer] ??= [];
		acc[slot.winningAdServer].push(slot);

		return acc;
	}, {} as Partial<Record<AdServer, Slot[]>>);


	Object.entries(slotsByWinningAdServer).forEach(([adServer, slotsForAdServer]: [AdServer, Slot[]]) => {
		ADMANAGERS[adServer].refreshSlots(slotsForAdServer);
	});
}

export function setUserPageOptions(userIds: AnyObject[]) {
	Object.values(getAdManagers()).forEach(adManager => {
		adManager.setUserPageOptions?.(userIds);
	});
}

export function updatePageTargeting() {
	Object.values(getAdManagers()).forEach(adManager => {
		adManager.updatePageTargeting();
	});
}

export function resizeSlot(slot: Slot, newSize: [number, number]) {
	ADMANAGERS[slot.winningAdServer].resizeSlot(slot, newSize);
}

export function removeSlot(slot: Slot) {
	Object.values(ADMANAGERS).forEach(adManager => {
		adManager.removeSlot?.(slot);
	});
}
